import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createHash, randomBytes } from "node:crypto";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin only");
}

function hashToken(token: string) {
  return createHash("sha256").update(token).digest("hex");
}

/** Admin: create an invitation. Returns the raw token (shown once). */
export const createInvitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        email: z.string().trim().email().max(255),
        role: z.enum(["admin", "analyst", "authority"]).default("analyst"),
        days: z.number().int().min(1).max(60).default(14),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const token = randomBytes(32).toString("base64url");
    const tokenHash = hashToken(token);
    const expiresAt = new Date(Date.now() + data.days * 86400_000).toISOString();
    const { data: row, error } = await supabaseAdmin
      .from("invitations")
      .insert({
        email: data.email.toLowerCase(),
        token_hash: tokenHash,
        role: data.role,
        expires_at: expiresAt,
        created_by: context.userId,
      })
      .select("id, email, role, expires_at, created_at")
      .single();
    if (error) throw new Error(error.message);
    return { invitation: row, token };
  });

/** Admin: list invitations. */
export const listInvitations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("invitations")
      .select("id, email, role, expires_at, used_at, created_at, created_by")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Admin: revoke an invitation (delete). */
export const revokeInvitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { error } = await supabaseAdmin
      .from("invitations")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Public: look up an invitation by token (returns email + role only). */
export const lookupInvitation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(20).max(200) }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const tokenHash = hashToken(data.token);
    const { data: row, error } = await supabaseAdmin
      .from("invitations")
      .select("email, role, expires_at, used_at")
      .eq("token_hash", tokenHash)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Invitation not found or already used.");
    if (row.used_at) throw new Error("This invitation has already been used.");
    if (new Date(row.expires_at) < new Date()) throw new Error("This invitation has expired.");
    return { email: row.email, role: row.role };
  });

/** Public: redeem an invitation by creating the user with the chosen password. */
export const acceptInvitation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        token: z.string().min(20).max(200),
        password: z.string().min(8).max(128),
        display_name: z.string().trim().max(120).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const tokenHash = hashToken(data.token);
    const { data: row, error: lookupErr } = await supabaseAdmin
      .from("invitations")
      .select("id, email, role, expires_at, used_at")
      .eq("token_hash", tokenHash)
      .maybeSingle();
    if (lookupErr) throw new Error(lookupErr.message);
    if (!row) throw new Error("Invitation not found.");
    if (row.used_at) throw new Error("This invitation has already been used.");
    if (new Date(row.expires_at) < new Date()) throw new Error("This invitation has expired.");

    const { data: created, error: createErr } = await supabaseAdmin.auth.admin.createUser({
      email: row.email,
      password: data.password,
      email_confirm: true,
      user_metadata: { display_name: data.display_name ?? null },
    });
    if (createErr) throw new Error(createErr.message);

    // The DB trigger consumes the invite and assigns the role automatically,
    // but enforce role one more time in case the trigger picked a different match.
    if (created.user) {
      await supabaseAdmin
        .from("profiles")
        .upsert(
          {
            user_id: created.user.id,
            email: created.user.email ?? row.email,
            display_name: data.display_name || row.email.split("@")[0],
            clearance_level: row.role === "admin" ? "L3" : "L1",
          },
          { onConflict: "user_id" },
        );
      await supabaseAdmin
        .from("user_roles")
        .upsert(
          { user_id: created.user.id, role: row.role },
          { onConflict: "user_id,role", ignoreDuplicates: true },
        );
      await supabaseAdmin
        .from("invitations")
        .update({ used_at: new Date().toISOString() })
        .eq("id", row.id);
    }

    return { ok: true, email: row.email };
  });